// REBEL — Social Section "Connect with RE₿EL" (additive).
// 4 cards: X, YouTube, Facebook, Official Website. Glassmorphism + neon hover,
// fully i18n via `t.social.*` (es / en / sk). Tap target opens in new tab.
import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { ExternalLink, Globe, Sparkles } from "lucide-react";

// X / Facebook / YouTube logos as inline SVG so we don't depend on brand kits.
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2H21l-6.55 7.49L22 22h-6.79l-4.74-6.21L4.6 22H2l7.01-8.02L2 2h6.97l4.32 5.71L18.244 2Zm-1.19 18.4h1.75L7.04 3.5H5.18l11.87 16.9Z" />
  </svg>
);
const YouTubeIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1c.4-1.9.5-3.8.5-5.8s-.1-3.9-.5-5.8ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z" />
  </svg>
);
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.14 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.57v1.88h2.78l-.44 2.91h-2.34V22c4.78-.8 8.44-4.94 8.44-9.94Z" />
  </svg>
);
// Telegram paper-plane mark (same outlined-fill convention as the
// X/YouTube/Facebook SVGs so it inherits color from `currentColor`).
const TelegramIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M21.94 4.27a1.5 1.5 0 0 0-1.96-1.5L2.84 9.45c-1.13.44-1.13 2.04 0 2.47l4.13 1.6 1.6 5.13c.2.65.99.86 1.5.42l2.45-2.16 4.3 3.16c.79.58 1.92.16 2.13-.8l3-13.96.0-.04ZM9.5 14.84l-.4 3.43-1.13-3.6 9.16-7.06-7.63 7.23Z"/>
  </svg>
);

// Visual descriptor per platform — colors used for neon ring + hover.
const CARDS = (t) => [
  {
    key: "x",
    name: "RE₿EL",
    handle: t.social.x.handle,
    desc: t.social.x.desc,
    url: "https://x.com/ShibaInuWorld_",
    Icon: XIcon,
    accent: "#ffffff",
    glow: "rgba(255,255,255,0.18)",
    bg: "rgba(255,255,255,0.02)",
    cta: t.social.follow,
  },
  {
    key: "youtube",
    name: "YouTube",
    handle: t.social.youtube.handle,
    desc: t.social.youtube.desc,
    url: "https://youtube.com/@satoshin_gmx",
    Icon: YouTubeIcon,
    accent: "#ff0033",
    glow: "rgba(255,0,51,0.28)",
    bg: "rgba(255,0,51,0.04)",
    cta: t.social.follow,
  },
  {
    key: "facebook",
    name: "Peter Stanga",
    handle: t.social.facebook.handle,
    desc: t.social.facebook.desc,
    url: "https://www.facebook.com/share/1Gt7FdeagG/",
    Icon: FacebookIcon,
    accent: "#1877f2",
    glow: "rgba(24,119,242,0.32)",
    bg: "rgba(24,119,242,0.04)",
    cta: t.social.follow,
  },
  {
    key: "telegram",
    name: "Telegram",
    handle: t.social.telegram.handle,
    desc: t.social.telegram.desc,
    url: "https://t.me/RebelBTCoficial",
    Icon: TelegramIcon,
    accent: "#2AABEE",
    glow: "rgba(42,171,238,0.32)",
    bg: "rgba(42,171,238,0.04)",
    cta: t.social.telegram.cta,
  },
  {
    key: "website",
    name: "RE₿EL AI Platform",
    handle: t.social.website.handle,
    desc: t.social.website.desc,
    url: "https://rebelbtc.com",
    Icon: Globe,
    accent: "#00d4ff",
    glow: "rgba(0,212,255,0.32)",
    bg: "rgba(0,212,255,0.04)",
    cta: t.social.visit,
  },
];

export const ConnectWithRebel = ({ id = "connect" }) => {
  const { t } = useLanguage();
  const cards = CARDS(t);

  return (
    <section
      id={id}
      className="relative w-full overflow-hidden border-y border-white/[0.05] bg-gradient-to-b from-zinc-950 via-black to-zinc-950 py-16 sm:py-20"
      data-testid="connect-with-rebel"
    >
      {/* Decorative glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 mx-auto h-[280px] max-w-3xl rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(50% 60% at 50% 0%, rgba(0,212,255,0.18) 0%, transparent 70%)" }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-cyan-200"
            data-testid="social-tag"
          >
            <Sparkles className="h-2.5 w-2.5" />
            ◈ Official channels
          </span>
          <h2 className="mt-4 text-3xl font-black tracking-tight sm:text-4xl lg:text-[2.6rem]">
            <span className="bg-gradient-to-r from-white via-cyan-100 to-orange-200 bg-clip-text text-transparent">
              {t.social.title}
            </span>
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/55 sm:text-[15px]">
            {t.social.subtitle}
          </p>
        </div>

        {/* Cards */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 xl:grid-cols-5">
          {cards.map((card, i) => (
            <SocialCard key={card.key} card={card} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const SocialCard = ({ card, index }) => {
  const Icon = card.Icon;
  return (
    <motion.a
      href={card.url}
      target="_blank"
      rel="noopener noreferrer me"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-950/70 p-5 backdrop-blur-md transition-all duration-300"
      style={{
        boxShadow: `0 0 0 1px transparent`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${card.accent}55`;
        e.currentTarget.style.boxShadow = `0 12px 36px ${card.glow}, 0 0 0 1px ${card.accent}40`;
        e.currentTarget.style.background = card.bg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
        e.currentTarget.style.boxShadow = "0 0 0 1px transparent";
        e.currentTarget.style.background = "rgba(9,9,11,0.7)";
      }}
      data-testid={`social-card-${card.key}`}
    >
      {/* Top row: icon + external badge */}
      <div className="flex items-start justify-between">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-xl border transition-all"
          style={{
            borderColor: `${card.accent}55`,
            background: `${card.accent}1a`,
            color: card.accent,
          }}
          aria-hidden
        >
          <Icon className="h-5 w-5" />
        </div>
        <ExternalLink className="h-3.5 w-3.5 text-white/30 transition-colors group-hover:text-white/70" />
      </div>

      {/* Name + handle */}
      <div className="mt-4">
        <p className="truncate text-sm font-black tracking-tight text-white">
          {card.name}
        </p>
        <p className="mt-0.5 truncate font-mono text-[11px] text-white/45">{card.handle}</p>
      </div>

      {/* Description */}
      <p className="mt-3 line-clamp-4 text-[12px] leading-relaxed text-white/55">
        {card.desc}
      </p>

      {/* CTA — fills remaining height */}
      <div className="mt-auto pt-4">
        <span
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border px-3 py-2 font-mono text-[10px] font-black uppercase tracking-[0.22em] transition-all"
          style={{
            borderColor: `${card.accent}55`,
            color: card.accent,
            background: `${card.accent}10`,
          }}
        >
          {card.cta} <ExternalLink className="h-3 w-3" />
        </span>
      </div>
    </motion.a>
  );
};

export default ConnectWithRebel;
